import Breadcrumb from '@/components/breadcrumb';
import { MapComponent } from '@components/common/map/map-component.tsx';
import { ConfirmationModal } from '@components/confirmation-modal.tsx';
import { InductionArea } from '@components/induction-area/induction-area.tsx';
import { CloudDownload, Error, NotListedLocation } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import { ActionPlanInduction } from '@sections/simulation/plan-induction.tsx';
import { RouteCardsList } from '@sections/simulation/route-card/route-cards-list.tsx';
import { SplitRouteModal } from '@sections/simulation/split-route/split-route.tsx';
import { useDeckAreas } from '@sections/simulation/use-deck-areas.ts';
import { useDeckDrawer } from '@sections/simulation/use-deck-drawer.ts';

import { useDeckPoints } from '@sections/simulation/use-deck-points.ts';
import { useDeckRoutes } from '@sections/simulation/use-deck-routes.ts';
import { useDeckStation } from '@sections/simulation/use-deck-station.ts';
import { useDeckStreetRoute } from '@sections/simulation/use-deck-street-route.ts';
import { Position } from 'geojson';
import { useEffect, useMemo, useRef } from 'react';
import { MapRef } from 'react-map-gl/maplibre';

import { useSimulationDetail } from './use-simulation-detail';

const tooltipStyle: React.CSSProperties = {
  position: 'absolute',
  zIndex: 1,
  pointerEvents: 'none',
};

export const SimulationDetailPage = () => {
  const hooks = useSimulationDetail();
  const mapRef = useRef<MapRef>(null);
  const pointsHooks = useDeckPoints(mapRef, hooks.plan?.routes);
  const areasHooks = useDeckAreas(hooks.plan?.areas);
  useEffect(() => {
    if (hooks.stationCoordinates && mapRef.current) {
      mapRef.current.flyTo({
        center: hooks.stationCoordinates,
        zoom: 11,
        duration: 1000,
      });
    }
  }, [hooks.stationCoordinates]);

  const routesHooks = useDeckRoutes({
    routes: hooks.plan?.routes,
    polylines: hooks.plan?.polylines,
    selectedRoutes: pointsHooks.selectedRoutes,
    hoveredRoute: pointsHooks.hoveredRoute,
  });
  const drawerHooks = useDeckDrawer(
    mapRef,
    hooks.plan?.routes,
    hooks.onMoveOrdersToRoute,
  );
  // Ruta actualmente enfocada (tomamos la primera de selectedRoutes)
  const focusedRoute = useMemo(
    () =>
      hooks.plan?.routes.find(
        (r) => r.name === pointsHooks.selectedRoutes[0],
      ) ?? null,
    [hooks.plan?.routes, pointsHooks.selectedRoutes],
  );

  // Coordenadas [lng, lat] de esa ruta, ordenadas por delivery_order
  const focusedRouteCoords: Position[] | null = useMemo(() => {
    if (!focusedRoute) return null;
    return [...focusedRoute.orders]
      .slice()
      .sort((a, b) => a.delivery_order - b.delivery_order)
      .map((o) => o.geometry.coordinates as Position);
  }, [focusedRoute]);

  const focusedRouteKey: string | null = focusedRoute ? focusedRoute.id : null;
  const MAPTILER_TOKEN = (import.meta as any).env.VITE_MAPTILER_API_KEY as
    | string
    | undefined;

  const streetRouteHooks = useDeckStreetRoute({
    coordinates: focusedRouteCoords,
    accessToken: MAPTILER_TOKEN ?? null,
    routeKey: focusedRouteKey,
  });

  const stationHooks = useDeckStation({
    position: hooks.stationCoordinates,
    idSuffix: hooks.plan?.plan_id,
    station_name: hooks.plan?.station ?? null,
  });

  const layers = [
    ...areasHooks.layers,
    ...routesHooks.layers,
    ...streetRouteHooks.layers,
    ...pointsHooks.layers,
    ...stationHooks.layers,
  ];

  return (
    <div className="flex flex-col h-full overflow-auto gap-0">
      <Breadcrumb
        sections={[
          { label: 'Simulaciones', link: '/' },
          { label: hooks.plan?.plan_name || 'Detalle de Simulación' },
        ]}
        description={`Detalle de la simulación. ${hooks.plan?.preset ? `(Preset: ${hooks.plan.preset})` : ''}`}
        actions={
          <div>
            <ActionPlanInduction
              plan={hooks.plan ?? undefined}
              selected={hooks.selected}
              onAcceptPlan={hooks.onAcceptPlan}
              onRejectPlan={hooks.onRejectPlan}
              startInduction={hooks.startInduction}
              loadingInduction={hooks.loadingInduction}
              onStartJoinRoutes={hooks.onStartJoinRoutes}
              isEditMode={drawerHooks.isEditMode}
              cancelSelection={drawerHooks.cancelSelection}
              startSelection={drawerHooks.startSelection}
            />
            <Tooltip title={'Descargar listado de ordenes sin ruta'}>
              <span>
                <Button
                  color="error"
                  onClick={hooks.onDownloadOutPlan}
                  startIcon={<NotListedLocation />}
                  disabled={!hooks.plan || !hooks.plan?.out_routes}
                >
                  {hooks.orders[1]}
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={'Descargar listado de rutas'}>
              <span>
                <Button
                  onClick={hooks.onDownloadPlan}
                  disabled={!hooks.plan || !hooks.plan?.routes}
                  color="primary"
                  startIcon={<CloudDownload />}
                >
                  {hooks.orders[0]}
                </Button>
              </span>
            </Tooltip>
          </div>
        }
      />
      {!hooks.plan && <LinearProgress />}
      <div className="flex flex-1 w-full h-[calc(100vh-64px)]">
        <div className="w-1/2 md:w-5/12 lg:w-1/5 xl:w-2/9 overflow-auto">
          {hooks.error && (
            <div className="w-full">
              <Alert color="warning" icon={<Error />}>
                {hooks.error}
              </Alert>
            </div>
          )}
          <RouteCardsList
            plan={hooks.plan ?? undefined}
            onSplit={hooks.onSplit}
            selected={hooks.selected}
            focusRoute={pointsHooks.focusRoute}
            onToggleAll={hooks.onToggleAll}
            onToggleSelection={hooks.onToggleSelection}
            onStartDisableRoute={hooks.onStartDisableRoute}
          />
        </div>
        <div className="relative flex-1">
          <div className="w-full h-[calc(100vh-64px-90px)] relative border-l border-[#CACACA]">
            <MapComponent mapRef={mapRef} layers={layers}>
              {pointsHooks.hoverInfo?.object && (
                <div
                  style={{
                    ...tooltipStyle,
                    left: pointsHooks.hoverInfo.x,
                    top: pointsHooks.hoverInfo.y,
                  }}
                  className="z-10! bg-white! text-xs -translate-x-1/2 -translate-y-full p-2 rounded shadow-md border border-gray-200 pointer-events-none"
                >
                  <p>
                    <strong>Guía 99Minutos</strong> ``
                    {pointsHooks.hoverInfo.object.properties.id} <br />
                    <strong>Guía Interna</strong>{' '}
                    {pointsHooks.hoverInfo.object.properties.internal_key}{' '}
                    <br />
                    <strong>Ruta:</strong>{' '}
                    {pointsHooks.hoverInfo.object.properties.route} <br />
                    <strong>Parada:</strong>{' '}
                    {pointsHooks.hoverInfo.object.properties.stop} <br />
                  </p>
                  <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 translate-y-full border-4 border-transparent border-t-white"></div>
                </div>
              )}
            </MapComponent>
          </div>
        </div>
      </div>
      {hooks.show_induction && hooks.plan && (
        <InductionArea
          onClose={hooks.closeInduction}
          routes={hooks.plan.routes
            .filter((r) => hooks.selected.includes(r.id))
            .map((r) => ({
              ...r,
              plan_id: hooks.plan!.plan_id,
            }))}
        />
      )}
      {hooks.forSplit && hooks.plan && (
        <SplitRouteModal
          planId={hooks.plan.plan_id}
          route={hooks.forSplit}
          onCancel={hooks.onCancelSplit}
          onSuccess={hooks.onAcceptSplit}
        />
      )}
      {hooks.showJoinRoutes && (
        <ConfirmationModal
          loading={hooks.joinRoutesIsLoading}
          onCancel={hooks.onCancelJoinRoutes}
          onSuccess={hooks.onConfirmJoinRoutes}
        >
          <Typography>
            ¿Estas seguro de unir las rutas seleccionadas?
          </Typography>
        </ConfirmationModal>
      )}
      {hooks.forDisableRoute && (
        <ConfirmationModal
          loading={hooks.disableLoading}
          onCancel={hooks.onCancelDisableRoute}
          onSuccess={hooks.onConfirmDisableRoute}
        >
          <Typography>¿Estas seguro desea desactivar la ruta?</Typography>
        </ConfirmationModal>
      )}
      {drawerHooks.trackings.length > 0 && hooks.plan && (
        <Dialog
          fullWidth
          open={true}
          maxWidth="xs"
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title"></DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Se trasladaran {drawerHooks.trackings.length} ordenes a la ruta
              seleccionada.
            </DialogContentText>
            <Select
              fullWidth
              label={'Ruta'}
              value={drawerHooks.route}
              onChange={(e) =>
                drawerHooks.onChangeRoute(e.target.value as string)
              }
            >
              {hooks.plan.routes.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.name}
                </MenuItem>
              ))}
            </Select>
            <List
              dense={true}
              sx={{
                width: '100%',
                position: 'relative',
                overflow: 'auto',
                maxHeight: 300,
              }}
            >
              {drawerHooks.trackings.map((sectionId) => (
                <ListItem key={`item-${sectionId}`}>
                  <ListItemText primary={sectionId} />
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={drawerHooks.onReject}
              variant="contained"
              disableElevation
              color="secondary"
              disabled={drawerHooks.isLoading}
            >
              Cancelar
            </Button>
            <LoadingButton
              loading={drawerHooks.isLoading}
              onClick={drawerHooks.onAccept}
              disabled={!drawerHooks.route}
              variant="contained"
              disableElevation
            >
              Aceptar
            </LoadingButton>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
};
